import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { Endpoints } from '@/api';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import IconChevron from '@/icons/Chevron.svg';
import { useActionTypeOrganizationWorkshops } from '@/models/actionType';
import { getUserWorkshops } from '@/models/user';
import { useCurrentSession } from '@/services/auth';
import { useDatabaseTranslation } from '@/services/i18n';
import { unique } from '@/utils/array';

import { AgreementPricesDialog } from '../shared/AgreementPricesDialog';

type ActionType =
  Endpoints['GET /action-types/workshop/:workshop-id']['response']['actionTypes'][number];
type PackActionType =
  Endpoints['GET /action-types/workshop/:workshop-id']['response']['packActionTypes'][number];

export const ServicesWorkshop = () => {
  const { t } = useLingui();
  const { _compare } = useDatabaseTranslation();
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const { currentSession } = useCurrentSession();

  const {
    data: { actionTypes, packActionTypes } = { actionTypes: [], packActionTypes: [] },
    isLoading,
  } = useActionTypeOrganizationWorkshops({
    workshopId: getUserWorkshops(currentSession)?.[0]?.id,
  });

  const actionAndPackActionTypes = [...actionTypes, ...packActionTypes];

  const organizations = unique(
    actionAndPackActionTypes.map((actionOrPack) => actionOrPack.organization).filter((x) => !!x),
    'id'
  );
  const selectedOrganization = organizations.find((org) => org.id === organizationId);

  const filteredActionAndPackActionTypes = actionAndPackActionTypes
    ?.filter((actionOrPack) => actionOrPack.organization.id === organizationId)
    .toSorted((a, b) => _compare(a.name, b.name));

  return (
    <Stack padding=" 0 0 24px 0" gap="24px">
      <Stack row alignItems="center" justifyContent="space-between" gap="8px">
        <Title size="200" as="h2">
          <Trans id="settings.services-workshop.table.label">Actions</Trans>
        </Title>
        <InputSelect
          aria-label={t({
            id: 'settings.services-workshop.organization.label',
            message: 'Organization',
          })}
          placeholder={t({
            id: 'settings.services-workshop.organization.placeholder',
            message: 'Select an organization',
          })}
          options={organizations}
          value={selectedOrganization}
          getOptionValue={(organization) => organization?.id}
          getOptionLabel={(organization) => organization?.name}
          onChange={(organization) => setOrganizationId(organization?.id ?? null)}
          style={{ width: '300px' }}
        />
      </Stack>
      <Table
        ariaLabel={t({ id: 'settings.services-workshop.table.label', message: 'Actions' })}
        columnWidths={['minmax(300px, auto)', '150px', 'minmax(300px, auto)']}
        variant="grid"
        bordered
      >
        <Header>
          <Row>
            <Column>
              <Trans id="settings.services-workshop.column.name">Name</Trans>
            </Column>
            <Column>
              <Trans id="settings.services-workshop.column.price-agreements">Prices</Trans>
            </Column>
            <Column>
              <Trans id="settings.services-workshop.column.product-categories">
                Product categories
              </Trans>
            </Column>
          </Row>
        </Header>
        <Body>
          {filteredActionAndPackActionTypes?.map((actionOrPack) => (
            <ActionRow key={actionOrPack.id} actionOrPack={actionOrPack} />
          ))}
          {!isLoading && !filteredActionAndPackActionTypes?.length && (
            <Row>
              <Cell isWholeRow justifyContent="center">
                <Trans id="settings.services-workshop.actions.select-organization">
                  Select an organization
                </Trans>
              </Cell>
            </Row>
          )}
        </Body>
      </Table>
    </Stack>
  );
};

const ActionRow = ({ actionOrPack }: { actionOrPack: ActionType | PackActionType }) => {
  const { _db } = useDatabaseTranslation();
  const [showActions, setShowActions] = useState(false);

  return (
    <Row>
      <Cell>
        <Stack gap="4px">
          <InlineText weight="medium">{_db(actionOrPack.name)}</InlineText>
          {'actions' in actionOrPack && (
            <>
              <Button
                variant="link-subtle"
                size="small"
                style={{ justifyContent: 'flex-start' }}
                onPress={() => setShowActions((prev) => !prev)}
              >
                <Trans id="components.services.pack-actions.view-details">View details</Trans>
                <IconChevron up={showActions} down={!showActions} />
              </Button>
              {showActions && (
                <ul>
                  {actionOrPack.actions.map((action) => (
                    <li key={action.id}>
                      <InlineText size="200" color="secondary">
                        {_db(action.name)}
                      </InlineText>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Stack>
      </Cell>
      <Cell justifyContent="center">
        <AgreementPricesDialog
          id={actionOrPack.id}
          name={_db(actionOrPack.name)}
          agreementPrices={actionOrPack.agreementPrices}
          type={'actions' in actionOrPack ? 'action' : 'pack-action'}
          mode="workshop"
        />
      </Cell>
      <Cell>
        <Stack>
          {actionOrPack.productAttributeValues.map((productAttributeValue) => (
            <span key={productAttributeValue.id}>
              <InlineText size="200" weight="medium">
                {_db(productAttributeValue.productAttribute.name)}
                <Trans id="_general.colon">:</Trans>{' '}
              </InlineText>
              <InlineText size="200" color="secondary">
                {_db(productAttributeValue.name)}
              </InlineText>
            </span>
          ))}
        </Stack>
      </Cell>
    </Row>
  );
};
